import { format } from 'date-fns'

import { CalendarItemKind } from '../../../../constants/calendar'
import {
  CALENDAR_GRID_LABEL_SEPARATOR,
  CalendarGridItemVariant,
} from '../../../../constants/calendarGrid'
import { DATE_API_DATA_FORMAT_SHORT } from '../../../../constants/date'
import { toUkTimezoneDate } from '../../../../utils/date'
import {
  CALENDAR_DAY_LOAD_TAG_COLORS,
  CALENDAR_ITEM_KIND_VARIANT,
  CALENDAR_LIST_SEPARATOR,
} from '../../Calendar.constants'
import {
  getCalendarRequestLabel,
  getIsCalendarAppointmentCancelled,
} from '../../Calendar.utils'

function getCalendarAssigneesLabel(assignees) {
  return assignees.map(assignee => assignee.name).join(CALENDAR_LIST_SEPARATOR)
}

// TODO: read the address from its CAL-01 §11.1 field once the spec lists it
// (TM-01-08).
function getCalendarFollowUpDetailsLabel({ assignees, request }) {
  return [getCalendarAssigneesLabel(assignees), request.address]
    .filter(Boolean)
    .join(CALENDAR_GRID_LABEL_SEPARATOR)
}

function getCalendarStepsLabel(steps) {
  return steps.map(step => step.title).join(CALENDAR_LIST_SEPARATOR)
}

function getCalendarTaskGroupTitle({ count, request, t }) {
  return [request.number, t('Calendar.StepsChipKey', { count })].join(
    CALENDAR_GRID_LABEL_SEPARATOR
  )
}

function getCalendarAppointmentAnchorLabel(anchor) {
  const anchorName = anchor.request?.number ?? anchor.building?.short_name

  return anchor.flat
    ? [anchorName, anchor.flat.name].join(CALENDAR_GRID_LABEL_SEPARATOR)
    : anchorName
}

function getCalendarLocalDate({ timezone, value }) {
  return format(toUkTimezoneDate(value, timezone), DATE_API_DATA_FORMAT_SHORT)
}

function getHasCalendarDayLoadLimit(load) {
  return load.limit !== null
}

function toCalendarGridDayLoad({ load, t }) {
  const params = { limit: load.limit, used: load.follow_up_count }

  return {
    ...params,
    isFull: load.is_full,
    laneLabel: t('Calendar.DayFullKey', params),
    loadLabel: t('Calendar.DayFullShortKey', params),
    monthLabel: t('Calendar.DayFullShortKey', params),
    summary: t('Calendar.DayLoadHeaderKey', params),
    tag: { ...CALENDAR_DAY_LOAD_TAG_COLORS, text: t('Calendar.FollowUpsKey') },
  }
}

function toCalendarGridTaskStep({ step: { done, id, title }, url }) {
  return { id, isDone: done, kind: CalendarItemKind.taskDeadline, title, url }
}

function toCalendarGridFollowUp({
  assignees,
  can_edit: canEdit,
  date,
  id,
  is_due: isDue,
  kind,
  request,
}) {
  return {
    canEdit,
    date,
    id,
    isAllDay: true,
    isArchived: request.is_archived,
    isCountedInDayLoad: true,
    isDraggable: canEdit,
    isDue,
    isOpenedDirectly: true,
    kind,
    popover: {
      secondaryLabel: getCalendarFollowUpDetailsLabel({ assignees, request }),
    },
    secondaryLabel: getCalendarAssigneesLabel(assignees),
    title: getCalendarRequestLabel(request),
    url: request.url,
    variant: CALENDAR_ITEM_KIND_VARIANT[kind],
  }
}

// TODO: align the follow_up_day fields with CAL-01 §11.1 once the spec lists
// them (TM-01-08).
function toCalendarGridFollowUpDay({ count, date, id, items, kind }, t) {
  return {
    aggregatedItems: items.map(toCalendarGridFollowUp),
    date,
    id,
    isAllDay: true,
    isCountedInDayLoad: true,
    kind,
    opensDayPopover: true,
    title: t('Calendar.FollowUpsChipKey', { count }),
    variant: CALENDAR_ITEM_KIND_VARIANT[kind],
  }
}

function toCalendarGridTaskDeadline({
  can_edit: canEdit,
  date,
  deadline,
  done,
  id,
  kind,
  request,
  step_index: stepIndex,
  step_total: stepTotal,
  title,
}) {
  return {
    date,
    deadline,
    id,
    isAllDay: true,
    isDone: done,
    isDraggable: canEdit,
    isOpenedDirectly: true,
    kind,
    request,
    secondaryLabel: request.number,
    stepIndex,
    stepTotal,
    title,
    url: request.url,
    variant: CALENDAR_ITEM_KIND_VARIANT[kind],
  }
}

function toCalendarGridTaskGroup({ count, date, id, items, kind, request }, t) {
  return {
    date,
    id,
    isAllDay: true,
    kind,
    opensDayPopover: true,
    popover: {
      subItems: items.map(step =>
        toCalendarGridTaskStep({ step, url: request.url })
      ),
    },
    secondaryLabel: getCalendarStepsLabel(items),
    title: getCalendarTaskGroupTitle({ count, request, t }),
    url: request.url,
    variant: CALENDAR_ITEM_KIND_VARIANT[kind],
  }
}

// TODO: open the appointment detail panel on click once it exists, instead of
// the grid card.
function toCalendarGridAppointment({
  item: {
    anchor,
    end_at: end,
    id,
    is_all_day: isAllDay,
    kind,
    start_at: start,
    status,
    title,
  },
  timezone,
}) {
  return {
    date: getCalendarLocalDate({ timezone, value: start }),
    end,
    id,
    isAllDay,
    isArchived: anchor.is_archived,
    isCancelled: getIsCalendarAppointmentCancelled(status),
    isStatusPinned: true,
    kind,
    lastDate: getCalendarLocalDate({ timezone, value: end }),
    secondaryLabel: getCalendarAppointmentAnchorLabel(anchor),
    start,
    title,
    url: anchor.request?.url ?? anchor.building?.url,
    variant: anchor.is_archived
      ? CalendarGridItemVariant.muted
      : CALENDAR_ITEM_KIND_VARIANT[kind],
  }
}

export function toCalendarGridItem({ item, t, timezone }) {
  switch (item.kind) {
    case CalendarItemKind.appointment:
      return toCalendarGridAppointment({ item, timezone })
    case CalendarItemKind.followUp:
      return toCalendarGridFollowUp(item)
    case CalendarItemKind.followUpDay:
      return toCalendarGridFollowUpDay(item, t)
    case CalendarItemKind.taskGroup:
      return toCalendarGridTaskGroup(item, t)
    default:
      return toCalendarGridTaskDeadline(item)
  }
}

export function toCalendarGridDayLoads({ dayLoad, t }) {
  return Object.fromEntries(
    dayLoad
      .filter(getHasCalendarDayLoadLimit)
      .map(load => [load.date, toCalendarGridDayLoad({ load, t })])
  )
}
